import { useEffect, useState } from "react";
import { PageSkeleton, type PageSkeletonVariant } from "@/components/ui/pageSkeletons";

/**
 * PageLoader — el hueco que se enseña mientras llega una PÁGINA.
 *
 * Used EVERYWHERE a slower load needs an in-between state, across all
 * devices and all profiles (admin/manager, perito, worker):
 *  - Inner Suspense fallbacks during page-to-page navigation (all layouts)
 *  - Per-page in-component loading states (Mapa, Equipo, Plagas, reports…)
 *
 * DEJÓ DE SER UN ANILLO (29-08-2026). Era un aro girando, y se describía a sí
 * mismo como «el ÚNICO indicador de carga de toda la app»: lo usan los `Suspense`
 * de los tres armazones —gestor, operario y perito— y media docena de páginas. O
 * sea que era el aro de todo.
 *
 * Un aro no dice nada. Gira igual con una lista de tres filas que con un informe
 * entero, ocupa un sitio que no se parece al que va a ocupar el contenido, y al
 * llegar la página TODO salta. Un esqueleto con la forma de lo que viene hace el
 * trabajo contrario: reserva el hueco, así que cuando el contenido aterriza no
 * se mueve nada, y de paso cuenta lo que se está cargando.
 *
 * LO QUE NO CAMBIA, y es deliberado:
 *  1. Los primeros ~350 ms no se pinta NADA. Una navegación instantánea no debe
 *     enseñar ni un aro ni un esqueleto: enseñar algo que se va antes de que el
 *     ojo lo registre es un parpadeo, y un parpadeo se lee como un fallo.
 *  2. El `min-h-[40vh]` se queda: es lo que impide que el armazón se encoja
 *     mientras no hay contenido.
 *
 * `variant` elige la FORMA. Quien lo use debería pasar la de su ruta; el valor
 * por defecto («list») es la forma más común de esta aplicación.
 */

const APPEAR_DELAY_MS = 350;
const MIN_VISIBLE_MS = 250;

export function PageLoader({ variant = "list" }: { variant?: PageSkeletonVariant } = {}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let mounted = true;
    let shownAt: number | null = null;

    const showTimer = setTimeout(() => {
      if (!mounted) return;
      shownAt = Date.now();
      setShow(true);
    }, APPEAR_DELAY_MS);

    return () => {
      mounted = false;
      clearTimeout(showTimer);
      // If we showed for less than the minimum, do nothing here — the
      // unmount itself naturally hides the loader. The min-visible
      // guarantee is mostly cosmetic (avoids a 1-frame flash); React
      // batching + the delay above already covers the common cases.
      void shownAt;
    };
  }, []);

  if (!show) {
    // Reserve space without rendering anything visible.
    return <div className="min-h-[40vh] w-full" aria-hidden="true" />;
  }

  return (
    <div
      role="status"
      aria-label="Loading"
      className="min-h-[40vh] w-full animate-in fade-in duration-200"
    >
      <PageSkeleton variant={variant} />
    </div>
  );
}

export default PageLoader;
